/**
 * Normalize a free-form time string typed by a user into canonical
 * `HH:mm` (24h, zero-padded). Returns `undefined` when the input
 * can't be coerced into a valid time.
 *
 * Rules:
 * - A single-digit hour is zero-padded on the left (`"1"` → `"01:00"`).
 * - A missing minute defaults to `"00"` (`"12"` → `"12:00"`).
 * - A single-digit minute is treated as the **tens** place, not the
 *   ones place — so `"12:2"` becomes `"12:20"`, optimized for typing
 *   speed (one keystroke for `:10`, `:20`, `:30`, …). This is the
 *   opposite of native `<input type="time">` left-padding, and is
 *   intentional.
 * - When no separator is present, the digits are split as
 *   `HHmm` / `HHm` / `HH` / `H` (`"1234"` → `"12:34"`,
 *   `"122"` → `"12:20"`).
 * - Out-of-range values (hour > 23, minute > 59) return `undefined`.
 *
 * Only `:` is supported as a separator. Whitespace at the ends is
 * trimmed; whitespace inside the value is rejected.
 */
const HH_MM_RE = /^([01]\d|2[0-3]):[0-5]\d$/
const DIGITS_RE = /^\d+$/

/**
 * Split the raw value into `[hourPart, minutePart]` — on the `:` when one
 * is present, otherwise positionally as `HHmm` / `HHm` / `HH` / `H`.
 */
function splitTimeParts(value: string): [string, string] | undefined {
  const colonIndex = value.indexOf(':')
  if (colonIndex >= 0) {
    const minutePart = value.slice(colonIndex + 1)
    if (minutePart.includes(':')) return undefined
    return [value.slice(0, colonIndex), minutePart]
  }
  if (!DIGITS_RE.test(value)) return undefined
  if (value.length <= 2) return [value, '']
  if (value.length <= 4) return [value.slice(0, 2), value.slice(2)]
  return undefined
}

/** A single-digit minute is the tens place (`"2"` → 20), per the rules above. */
function expandMinutePart(minutePart: string): number {
  if (minutePart.length === 0) return 0
  if (minutePart.length === 1) return Number(minutePart) * 10
  return Number(minutePart)
}

export function normalizeTimeString(input: string): string | undefined {
  const trimmed = input.trim()
  if (!trimmed) return undefined
  if (HH_MM_RE.test(trimmed)) return trimmed

  const parts = splitTimeParts(trimmed)
  if (!parts) return undefined
  const [hourPart, minutePart] = parts

  if (!DIGITS_RE.test(hourPart) || hourPart.length > 2) return undefined
  if (minutePart && !DIGITS_RE.test(minutePart)) return undefined
  if (minutePart.length > 2) return undefined

  const hour = Number(hourPart)
  if (hour < 0 || hour > 23) return undefined

  const minute = expandMinutePart(minutePart)
  if (minute < 0 || minute > 59) return undefined

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}
